import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Autocomplete,
  TextField,
  Box,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ClearIcon from '@mui/icons-material/Clear';
import { useGetNestedCareersMutation } from 'src/pages/CareerHistory/CareerHistorySlice';

const CareerAutoComplete = ({
  value,
  onChange,
  placeholder = 'Start typing to find a job title...',
  loading: externalLoading = false,
  fieldIndex,
  buttonColor,
  ...props
}) => {
  const [options, setOptions] = useState([]);

  // Debug log when options change
  useEffect(() => {}, [options]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const [getNestedCareers] = useGetNestedCareersMutation();
  const abortControllerRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const currentRequestRef = useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  // Helper function to get padding based on career group and screen size
  const getPaddingLeft = (group) => {
    if (isMobile) {
      return group === 'detailed' ? '20px' : '8px';
    }
    if (isTablet) {
      return group === 'detailed' ? '26px' : '10px';
    }
    if (isDesktop) {
      return group === 'detailed' ? '36px' : '14px';
    }
    return group === 'detailed' ? '32px' : '12px';
  };

  // Process careers to show parent-child relationships
  const processCareerData = useCallback((careers) => {
    const processedOptions = [];
    const addedIds = new Set();

    careers.forEach((career) => {
      // Add broad career if not already added
      if (!addedIds.has(career._id)) {
        processedOptions.push({
          ...career,
          group: 'broad',
          title: career.title,
        });
        addedIds.add(career._id);
      }

      // Add specialised roles
      if (career.specialisedRoles && career.specialisedRoles.length > 0) {
        career.specialisedRoles.forEach((role) => {
          if (!addedIds.has(role._id)) {
            processedOptions.push({
              ...role,
              group: 'detailed',
              title: role.title,
            });
            addedIds.add(role._id);
          }
        });
      }
    });

    return processedOptions;
  }, []);

  // Fetch careers with debouncing and abort previous requests
  const fetchCareers = useCallback(
    async (searchTerm = '', isDropdownOpen = false) => {
      // Create unique request ID
      const requestId = Date.now() + Math.random();
      currentRequestRef.current = requestId;

      try {
        setIsLoading(true);
        setOptions([]); // Clear previous options immediately

        const response = await getNestedCareers({
          searchTerm: searchTerm.trim(),
        });

        // Only update if this is still the latest request
        if (currentRequestRef.current === requestId) {
          const careersArray = response?.data || response || [];
          const processedData = processCareerData(careersArray);
          setOptions(processedData);
        }
      } catch (error) {
        // Only update if this is still the latest request
        if (currentRequestRef.current === requestId) {
          console.error('Error fetching careers:', error);
          setOptions([]);
        }
      } finally {
        // Only update loading if this is still the latest request
        if (currentRequestRef.current === requestId) {
          setIsLoading(false);
        }
      }
    },
    [getNestedCareers, processCareerData]
  );

  // Debounced search effect
  useEffect(() => {
    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Don't search if input matches selected value (user isn't typing, just showing selection)
    const isShowingSelectedValue =
      value && (value.label === inputValue || value.title === inputValue);

    // Only search if there's input or dropdown is open, and not just showing selected value
    if ((inputValue.trim() || isOpen) && !isShowingSelectedValue) {
      debounceTimerRef.current = setTimeout(() => {
        fetchCareers(inputValue, isOpen);
      }, 300);
    } else if (!inputValue.trim() && !isOpen) {
      setOptions([]);
      setIsLoading(false);
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [inputValue, isOpen, fetchCareers, value]);
  // Fix ESLint: Unexpected block statement surrounding arrow body
  // Move the returned value immediately after the =>
  // Before:
  // renderOption={(optionProps, option) => (
  //   <li>...</li>
  // )}
  // After:
  // renderOption={(optionProps, option) => <li>...</li>}
  // Cleanup on unmount
  useEffect(
    () => () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      currentRequestRef.current = null;
    },
    []
  );

  // Handle dropdown open
  const handleOpen = () => {
    setIsOpen(true);
    if (!inputValue.trim() && options.length === 0) {
      fetchCareers('', true);
    }
  };

  // Handle dropdown close
  const handleClose = () => {
    setIsOpen(false);
  };

  // Handle input change
  const handleInputChange = (event, newInputValue, reason) => {
    if (reason === 'input') {
      setInputValue(newInputValue);
    } else if (reason === 'reset' && !newInputValue) {
      setInputValue('');
    }
  };

  // Update input value when value changes
  useEffect(() => {
    if (value && (value.label || value.title)) {
      setInputValue(value.label || value.title);
    } else if (!value) {
      setInputValue('');
    }
  }, [value]);

  // Handle selection change
  const handleChange = (event, newValue) => {
    if (onChange) {
      onChange(newValue);
    }
  };

  const isLoadingState = isLoading || externalLoading;

  return (
    <Box
      className="career-autocomplete-wrapper"
      sx={{
        '&.career-autocomplete-wrapper': {
          width: '100% !important',
          maxWidth: 'none !important',
          minWidth: 'auto !important',
          '& .MuiAutocomplete-root': {
            width: '100% !important',
            maxWidth: 'none !important',
            minWidth: 'auto !important',
          },
          '& .MuiTextField-root': {
            width: '100% !important',
            maxWidth: 'none !important',
            minWidth: 'auto !important',
          },
        },
      }}
    >
      <Autocomplete
        {...props}
        open={isOpen}
        onOpen={handleOpen}
        onClose={handleClose}
        value={value}
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        options={options}
        loading={isLoadingState}
        getOptionLabel={(option) => option?.title || option?.label || ''}
        disableClearable
        isOptionEqualToValue={(option, val) =>
          option?._id === val?._id || option?._id === val?.value || option?.title === val?.label
        }
        filterOptions={(x) => x} // Disable client-side filtering
        componentsProps={{
          paper: {
            sx: {
              maxHeight: { xs: '200px', sm: '250px', md: '300px', lg: '350px', xl: '400px' },
              fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem', lg: '0.95rem', xl: '1rem' },
              '& .MuiAutocomplete-listbox': {
                padding: 0,
                '& .MuiAutocomplete-option': {
                  padding: 0,
                  minHeight: 'auto',
                },
              },
            },
          },
          popper: {
            sx: {
              '& .MuiPaper-root': {
                minWidth: 'unset !important',
                width: 'unset !important',
              },
            },
            modifiers: [
              {
                name: 'sameWidth',
                enabled: true,
                phase: 'beforeWrite',
                requires: ['computeStyles'],
                fn: ({ state }) => {
                  state.styles.popper.width = `${state.rects.reference.width}px`;
                },
              },
            ],
          },
        }}
        noOptionsText={(() => {
          if (isLoadingState) return 'Loading...';
          if (inputValue.trim()) return 'No careers found';
          return isMobile ? 'Start typing...' : 'Start typing to find a job title...';
        })()}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            variant="outlined"
            fullWidth
            sx={{
              width: '100%',
              minWidth: { xs: '280px', sm: '350px', md: '400px', lg: '450px', xl: '500px' },
              maxWidth: '100%',
              '& .MuiOutlinedInput-root': {
                fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1rem', lg: '1.05rem', xl: '1.1rem' },
                paddingRight: { xs: '44px', sm: '52px', md: '56px', lg: '60px', xl: '64px' },
                minHeight: { xs: '40px', sm: '44px', md: '48px', lg: '52px', xl: '56px' },
                width: '100%',
                backgroundColor: '#fff',
              },
              '& .MuiInputBase-input': {
                padding: {
                  xs: '10px 12px',
                  sm: '12px 14px',
                  md: '14px 16px',
                  lg: '16px 18px',
                  xl: '18px 20px',
                },
                fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1rem', lg: '1.05rem', xl: '1.1rem' },
                width: '100%',
              },
              '& .MuiAutocomplete-endAdornment': {
                right: { xs: '44px', sm: '52px', md: '56px', lg: '60px', xl: '64px' },
              },
              ...props.sx,
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  <Box
                    sx={{
                      backgroundColor: buttonColor,
                      borderTopRightRadius: '8px',
                      borderBottomRightRadius: '8px',
                      width: { xs: '44px', sm: '52px', md: '56px', lg: '60px', xl: '64px' },
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      bottom: 0,
                      zIndex: 1,
                      pointerEvents: value ? 'auto' : 'none',
                      cursor: value ? 'pointer' : 'default',
                    }}
                    onClick={
                      value
                        ? () => {
                            onChange(null);
                            setInputValue('');
                          }
                        : undefined
                    }
                  >
                    {(() => {
                      if (isLoadingState) {
                        return <CircularProgress color="inherit" size={20} />;
                      }
                      if (value) {
                        return (
                          <ClearIcon
                            sx={{
                              color: '#666',
                              fontSize: {
                                xs: '18px',
                                sm: '20px',
                                md: '22px',
                                lg: '24px',
                                xl: '26px',
                              },
                              marginRight: {
                                xs: '4px',
                                sm: '6px',
                                md: '8px',
                                lg: '10px',
                                xl: '12px',
                              },
                            }}
                          />
                        );
                      }
                      return (
                        <ExpandMoreIcon
                          sx={{
                            color: '#000',
                            fontSize: {
                              xs: '28px',
                              sm: '32px',
                              md: '36px',
                              lg: '40px',
                              xl: '44px',
                            },
                          }}
                        />
                      );
                    })()}
                  </Box>
                </>
              ),
            }}
          />
        )}
        renderOption={(optionProps, option) => (
          <li
            {...optionProps}
            key={option._id}
            style={{
              display: 'block',
              width: '100%',
              paddingLeft: getPaddingLeft(option.group),
              fontSize: isMobile ? '0.85rem' : '0.95rem',
              color: '#222',
              textAlign: 'left',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              padding: isMobile ? '8px 12px' : '12px 16px',
            }}
          >
            <div
              style={{
                fontWeight: option.group === 'broad' ? 600 : 400,
                fontSize: (() => {
                  if (option.group === 'broad') {
                    return isMobile ? '0.8rem' : '0.9rem';
                  }
                  return isMobile ? '0.7rem' : '0.75rem';
                })(),
                textAlign: 'left',
                lineHeight: isMobile ? '1.3' : '1.4',
              }}
            >
              {option.title}
            </div>
          </li>
        )}
      />
    </Box>
  );
};

CareerAutoComplete.propTypes = {
  value: PropTypes.shape({
    _id: PropTypes.string,
    value: PropTypes.string,
    title: PropTypes.string,
    label: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  loading: PropTypes.bool,
  fieldIndex: PropTypes.number,
  buttonColor: PropTypes.string,
  sx: PropTypes.object,
};

CareerAutoComplete.defaultProps = {
  value: null,
  placeholder: 'Start typing to find a job title...',
  loading: false,
  fieldIndex: 0,
  buttonColor: undefined,
};

export default CareerAutoComplete;
